import type { RoomRateSelection } from '../surcharges/surchargeUtils';

import type { RoomRateEgsPlan } from './buildEgsApplyPlan';
import type { EgsApplyCandidate } from './egsApplyCandidates';
import { getRatePlanId, getRatePlanName } from './egsRatePlans';

export interface EgsApplyRowRecord {
  isSelected: boolean;
  roomLabel: string;
}

export interface EgsApplyRow {
  plan?: RoomRateEgsPlan;
  /** Two rate plans can share a name, so grouping keys on this and only labels with the name. */
  ratePlanId: string;
  ratePlanName: string;
  records: Array<EgsApplyRowRecord>;
  roomRateId: string;
  roomTypeName: string;
}

/** A row the review step kept, which by then has a plan: nothing ticked against it means no plan. */
export type EgsReviewRow = EgsApplyRow & { plan: RoomRateEgsPlan };

function getRoomRateLabels(selection: RoomRateSelection) {
  return {
    ratePlanId: getRatePlanId(selection),
    ratePlanName: getRatePlanName(selection),
    roomTypeName: selection.roomType.name,
  };
}

/**
 * Wider than the set that currently has a plan on purpose: a surcharge awaiting a pick starts ticked to
 * nothing, so keying off the plans would hide the very room rate the pick is about.
 */
export function buildEgsApplyRows({
  candidates,
  plans,
  selectedRoomRateIdsByRoomLabel,
}: {
  candidates: Array<EgsApplyCandidate>;
  plans: Array<RoomRateEgsPlan>;
  selectedRoomRateIdsByRoomLabel: Record<string, Array<string>>;
}): Array<EgsApplyRow> {
  const planByRoomRateId = new Map(plans.map((plan) => [plan.roomRateId, plan]));
  const rowsByRoomRateId = new Map<string, EgsApplyRow>();

  for (const candidate of candidates) {
    const selectedIds = new Set(selectedRoomRateIdsByRoomLabel[candidate.roomLabel] ?? []);

    for (const selection of candidate.candidates) {
      const roomRateId = String(selection.roomRate.id);
      const row = rowsByRoomRateId.get(roomRateId) ?? {
        plan: planByRoomRateId.get(roomRateId),
        records: [],
        roomRateId,
        ...getRoomRateLabels(selection),
      };

      row.records.push({ isSelected: selectedIds.has(roomRateId), roomLabel: candidate.roomLabel });

      rowsByRoomRateId.set(roomRateId, row);
    }
  }

  return Array.from(rowsByRoomRateId.values()).sort(
    (a, b) => a.roomTypeName.localeCompare(b.roomTypeName) || a.ratePlanName.localeCompare(b.ratePlanName),
  );
}
